import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import * as Sentry from "@sentry/browser";

import { LoginUser } from "#interfaces/auth/login-user.interface";
import { SessionUser } from "#interfaces/auth/session-user.interface";
import { LogoutResponse } from "#interfaces/http-response/responses/http-responses";
import { AuthUtils } from "#root/core/auth/auth.utils";
import { UserStorageService } from "#root/core/user/user.service";
import { HeadersHttpService } from "#utils/http-headers/http-headers.service";
import { deployVersion } from "environments/deploy-version";
import { environment } from "environments/environment";
import {
  BehaviorSubject,
  catchError,
  Observable,
  of,
  shareReplay,
  switchMap,
  throwError,
} from "rxjs";

import {
  getUserPermissionMap,
  Permissions,
} from "#root/shared/enums/permission.enum";
import { FeatureFlagEnum } from "#root/config/enums/feature-flag.enum";
import { FeatureFlagService } from "#root/config/services/feature-flag/feature-flag.service";
import { DashboardService } from "#services/dashboard/dashboard.service";
import { PaymentOrdersService } from "#services/payment-orders/payment-orders.services";
import { User } from "../user/user.types";

const DEPLOY_VERSION_ASSET = "/assets/deploy-version.ts";
const DEPLOY_VERSION_PATTERN = /deployVersion(?:\s*:[^=]+)?\s*=\s*'([^']+)'/;
const PRESERVED_STORAGE_KEYS: string[] = [
  "remember",
  "rememberUser",
  "neverShowAgainItems",
  "computed-flow",
];
const PRESERVED_STORAGE_KEY_PREFIXES: string[] = ["fuse-config-"];

interface LoggedInUser {
  id: string;
  permissionsMap: ReturnType<typeof getUserPermissionMap>;
}

@Injectable()
export class AuthService {
  private readonly loggedInUser: BehaviorSubject<LoggedInUser> =
    new BehaviorSubject(null);

  private _isRefreshingToken = false;
  private _refreshTokenCall$: Observable<boolean> | null = null;
  private readonly _newVersionAvailable: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public get newVersionAvailable$(): Observable<boolean> {
    return this._newVersionAvailable.asObservable();
  }

  public get loggedInUser$(): Observable<LoggedInUser> {
    return this.loggedInUser.asObservable();
  }
private _forcePasswordChange = new BehaviorSubject<boolean>(false);

forcePasswordChange$ = this._forcePasswordChange.asObservable();
  public get loggedInUserSnapshot(): LoggedInUser {
    return this.loggedInUser.getValue();
  }

  private _authenticated: boolean = false;
  /**
   * Constructor
   */
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _userService: UserStorageService,
    private readonly headerService: HeadersHttpService,
    private readonly _router: Router,
    private readonly dashboardService: DashboardService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly paymentOrdersService: PaymentOrdersService,
  ) {}
  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------
  /**
   * Setter & getter for access token
   */
  set accessToken(token: string) {
    localStorage.setItem("accessToken", btoa(token));
  }

  get accessToken(): string {
    let token: string = localStorage.getItem("accessToken");
    if (token != null && token != "") {
      return atob(token);
    } else {
      return "";
    }
  }

  set refreshToken(token: string) {
    localStorage.setItem("refreshToken", btoa(token));
  }

  get refreshToken(): string {
    const token = localStorage.getItem("refreshToken");
    return token ? atob(token) : "";
  }
  set session(data: SessionUser) {
    let avatarUrl: string;
    if (data.photo != null && data.photo != "string") {
      avatarUrl = data.photo;
    } else {
      avatarUrl =
        "https://defc.ulpgc.es/wp-content/themes/defc-child/images/avatar.jpg";
    }
    const userDetails: User = {
      id: data.id,
      name: data.fullName,
      email: data.email,
      avatar: avatarUrl,
      status: "Logueado",
    };
    sessionStorage.setItem("userDetails", JSON.stringify(userDetails));
    localStorage.setItem("idUser", String(btoa(data.id)));
    localStorage.setItem("userName", String(data.userName));
    localStorage.setItem("rol", data.role);
    localStorage.setItem("email", data.email);
    localStorage.setItem(
      "idProject",
      btoa(data.projects.map((project) => project.id).join(",")),
    );
    localStorage.setItem("expirationDate", `${data.expirationDate}`);
    localStorage.setItem("passwordChangedBySelf", String(data.passwordChangedBySelf));
    if (data.refreshToken) {
      this.refreshToken = data.refreshToken;
    }
    localStorage.setItem("fullName", data.fullName);
    localStorage.setItem("mainProjectId", data.mainProjectId.toString());
    localStorage.setItem("signature", data.signature);
    localStorage.setItem("projects", JSON.stringify(data.projects));
    localStorage.setItem("permissions", JSON.stringify(data.permissions));

    localStorage.setItem(
      "userPermissionMap",
      JSON.stringify(
        getUserPermissionMap({
          user: {
            fullName: data.fullName,
            id: data.id,
            permissions: data.permissions as unknown as Permissions[],
          },
        }),
      ),
    );
    this.dashboardService.SetCurrentProject(data.mainProjectId);

    if (data.photo != null) {
      localStorage.setItem("avatar", data.photo);
    } else {
      localStorage.setItem(
        "avatar",
        "https://defc.ulpgc.es/wp-content/themes/defc-child/images/avatar.jpg",
      );
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Observable<any> {
    const baseUrl: string = environment.baseUrl + "User/ForgotPassword";
    const response = this._httpClient
      .post(
        baseUrl,
        { email: email },
        { headers: this.headerService.getHeadersPublic() },
      )
      .pipe(
        switchMap((resp) => {
          return of(resp);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
    return response;
  }
  /**
   * Reset password
   *
   * @param password
   */
  resetPassword(payload: {
  token: string;
  newPassword: string;
}): Observable<any> {
  return this._httpClient.post(
    `${environment.baseUrl}User/ResetPassword`,
    payload
  );
}

  updatePassword(password: string): Observable<any> {
    const userId: string = atob(localStorage.getItem("idUser"));
    const baseUrl: string = `${environment.baseUrl}User/ChangePassword/${userId}`;
    const response = this._httpClient.put(
      baseUrl,
      { id: userId, password: password },
      { headers: this.headerService.getHeadersPrivate() },
    );
    return response;
  }
  
      ChangeProfilePassword(
        currentPassword: string,
        password: string
    ): Observable<any> {
        const baseUrl: string = `${environment.baseUrl}User/ChangeProfilePassword`;
        const response = this._httpClient.put(
            baseUrl,
            { currentPassword: currentPassword, NewPassword: password },
      { headers: this.headerService.getHeadersPrivate() },
        );
        return response;
    }

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(credentials: { email: string; password: string }): Observable<any> {
    const baseUrl: string = environment.baseUrl + "User/Login";
    const requiredLogin: LoginUser = {
      email: credentials.email,
      password: credentials.password,
      userName: null,
      ipAddress: null,
      lat: null,
      lng: null,
      deviceInfo: null,
    };
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError(() => new Error("User is already logged in."));
    }

    const response = this._httpClient.post(baseUrl, requiredLogin).pipe(
      switchMap((response: any) => {
        // Store the access token in the local storage
        this.accessToken = response.data.token;
        // Set the authenticated flag to true
        this._authenticated = true;
        //Guardar Usuario con los servicios propios
        this.session = response.data;
        this.loggedInUser.next({
          id: response.data.id,
          permissionsMap: getUserPermissionMap({
            user: {
              fullName: response.data.fullName,
              id: response.data.id,
              permissions: response.data
                .permissions as unknown as Permissions[],
            },
          }),
        });
        // Store the user on the user service
        this._userService.user = {
          id: response.data.id,
          name: response.data.fullName,
          email: response.data.email,
          avatar:
            response.data.photo ??
            "https://defc.ulpgc.es/wp-content/themes/defc-child/images/avatar.jpg",
          status: "Logueado",
          permissions: response.data.permissions,
          userPermissionMap: getUserPermissionMap({
            user: {
              fullName: response.data.fullName,
              id: response.data.id,
              permissions: response.data
                .permissions as unknown as Permissions[],
            },
          }),
        };

        // Save user Sentry
        Sentry.setUser({
          id: response.data.id,
          email: response.data.email,
          username: response.data.fullName,
        });

        this.startBackgroundInvoiceVerification();

        // Return a new observable with the response
        return of(response);
      }),
    );
    return response;
  }

  private startBackgroundInvoiceVerification(): void {
    if (!this.featureFlagService.isEnabled(FeatureFlagEnum.ERP_INTEGRATION)) return;
    if (!this.loggedInUserSnapshot?.permissionsMap?.CanSyncPaymentOrderInvoice) return;

    this.paymentOrdersService.VerifyPendingInvoices().subscribe({ error: () => {} });
  }

//Login with Google
signInWithGoogle(idToken: string): Observable<any> {
  const baseUrl = environment.baseUrl + 'User/LoginWithGoogle';

  return this._httpClient.post(baseUrl, { idToken }).pipe(
    switchMap((response: any) => {
      this.accessToken = response.data.token;
      this._authenticated = true;
      this.session = response.data;

      this.loggedInUser.next({
        id: response.data.id,
        permissionsMap: getUserPermissionMap({
          user: {
            fullName: response.data.fullName,
            id: response.data.id,
            permissions: response.data.permissions as unknown as Permissions[],
          },
        }),
      });

      this._userService.user = {
        id: response.data.id,
        name: response.data.fullName,
        email: response.data.email,
        avatar: response.data.photo ??
          'https://defc.ulpgc.es/wp-content/themes/defc-child/images/avatar.jpg',
        status: 'Logueado',
        permissions: response.data.permissions,
        userPermissionMap: getUserPermissionMap({
          user: {
            fullName: response.data.fullName,
            id: response.data.id,
            permissions: response.data.permissions as unknown as Permissions[],
          },
        }),
      };

      Sentry.setUser({
        id: response.data.id,
        email: response.data.email,
        username: response.data.fullName,
      });

      this.startBackgroundInvoiceVerification();

      return of(response);
    }),
  );
}

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<boolean> {
    // Sign in using the token
    const token = atob(localStorage.getItem("accessToken"));
    if (token.length > 3) {
      this._authenticated = true;
          localStorage.setItem(
            "passwordChangedBySelf",
            localStorage.getItem("passwordChangedBySelf") ?? "false"
        );
      this._userService.user = {
        id: atob(localStorage.getItem("idUser")),
        name: localStorage.getItem("fullName"),
        email: localStorage.getItem("email"),
        avatar: localStorage.getItem("avatar"),
        permissions:
          JSON.parse(localStorage.getItem("permissions") ?? "[]") ?? [],
        userPermissionMap: JSON.parse(
          localStorage.getItem("userPermissionMap"),
        ),
        status: "Logueado",
      };
      this.loggedInUser.next({
        id: atob(localStorage.getItem("idUser")),
        permissionsMap: getUserPermissionMap({
          user: {
            fullName: localStorage.getItem("fullName"),
            id: atob(localStorage.getItem("idUser")),
            permissions:
              JSON.parse(localStorage.getItem("permissions") ?? "[]") ?? [],
          },
        }),
      });
      return of(true);
    } else {
      return of(false);
    }
  }
  /**
   * Refresh the access token using the stored refresh token.
   * Returns true if the refresh was successful, false otherwise.
   * Concurrent callers share the same in-flight HTTP call to avoid token rotation conflicts.
   */
  refreshUserToken(): Observable<boolean> {
    if (this._isRefreshingToken && this._refreshTokenCall$) {
      return this._refreshTokenCall$;
    }

    const storedRefreshToken = this.refreshToken;
    if (!storedRefreshToken) {
      return of(false);
    }

    this._isRefreshingToken = true;
    const baseUrl = `${environment.baseUrl}User/RefreshToken`;

    this._refreshTokenCall$ = this._httpClient
      .post(baseUrl, { token: storedRefreshToken })
      .pipe(
        switchMap((response: any) => {
          this._isRefreshingToken = false;
          this._refreshTokenCall$ = null;
          this.accessToken = response.data.token;
          this.refreshToken = response.data.refreshToken;
          localStorage.setItem("expirationDate", `${response.data.expirationDate}`);
          return of(true);
        }),
        catchError(() => {
          this._isRefreshingToken = false;
          this._refreshTokenCall$ = null;
          return of(false);
        }),
        shareReplay(1)
      );

    return this._refreshTokenCall$;
  }

  /**
   * Sign out
   */
  signOut(): Observable<any> {
    // Save user Sentry
    Sentry.setUser(null);

    const sesiongStorage = AuthUtils.isTokenExpired(this.accessToken);
    if (sesiongStorage) {
      const idUser: string = atob(localStorage.getItem("idUser"));
      const baseUrl: string = `${environment.baseUrl}User/Logout/${idUser}`;
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("idUser");
      localStorage.removeItem("userName");
      localStorage.removeItem("email");
      localStorage.removeItem("rol");
      localStorage.removeItem("projects");
      localStorage.removeItem("idProject");
      localStorage.removeItem("expirationDate");
      localStorage.removeItem("fullName");
      localStorage.removeItem("avatar");
      localStorage.removeItem("mainProjectId");
      localStorage.removeItem("selectedProjectId");
      sessionStorage.removeItem("userDetails");
      sessionStorage.removeItem("permissions");
      sessionStorage.removeItem("userPermissionMap");
      const response = this._httpClient
        .put<LogoutResponse>(baseUrl, {
          headers: this.headerService.getHeadersPublic(),
        })
        .pipe(
          switchMap((response: LogoutResponse) => {
            // Set the authenticated flag to false
            this._authenticated = false;
            this._router.navigate(["/sign-out"]);
            this._userService.user = null;
            this.loggedInUser.next(null);
            return of(true);
          }),
          catchError((err) => {
            this._authenticated = false;
            this._userService.user = null;
            this.loggedInUser.next(null);
            return of(true);
          }),
        );
      return response;
    } else {
      const idUser: string = atob(localStorage.getItem("idUser"));
      const baseUrl: string = `${environment.baseUrl}User/Logout/${idUser}`;
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("idUser");
      localStorage.removeItem("userName");
      localStorage.removeItem("email");
      localStorage.removeItem("rol");
      localStorage.removeItem("expirationDate");
      localStorage.removeItem("fullName");
      localStorage.removeItem("avatar");
      localStorage.removeItem("projects");
      localStorage.removeItem("idProject");
      localStorage.removeItem("mainProjectId");
      localStorage.removeItem("selectedProjectId");
      sessionStorage.removeItem("userDetails");
      sessionStorage.removeItem("permissions");
      sessionStorage.removeItem("userPermissionMap");
      const response = this._httpClient
        .put<LogoutResponse>(baseUrl, {
          headers: this.headerService.getHeadersPublic(),
        })
        .pipe(
          switchMap((response: LogoutResponse) => {
            // Set the authenticated flag to false
            this._authenticated = false;
            this._router.navigate(["/sign-out"]);
            this._userService.user = null;
            this.loggedInUser.next(null);
            return of(response);
          }),
          catchError((err) => {
            this._authenticated = false;
            this._userService.user = null;
            this.loggedInUser.next(null);
            return of(true);
          }),
        );
      return response;
    }
  }

  /**
   * Sign up
   *
   * @param user
   */
  signUp(user: {
    name: string;
    email: string;
    password: string;
    company: string;
  }): Observable<any> {
    return this._httpClient.post("api/auth/sign-up", user);
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: {
    email: string;
    password: string;
  }): Observable<any> {
    return this._httpClient.post("api/auth/unlock-session", credentials);
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check if the user is logged in
    if (this._authenticated) {
      return of(true);
    }
    // Check the access token availability
    if (!this.accessToken) {
      return of(false);
    }
    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      // If we have a refresh token, attempt to refresh before giving up
      if (this.refreshToken) {
        return this.refreshUserToken().pipe(
          switchMap((refreshed) => {
            if (refreshed) {
              return this.signInUsingToken();
            }
            return of(false);
          })
        );
      }
      return of(false);
    }
    // If the access token exists and it didn't expire, sign in using it
    return this.signInUsingToken();
  }

  clearStorageForNewVersion(): void {
    const preserved: Map<string, string> = new Map<string, string>();

    for (let index = 0; index < localStorage.length; index++) {
      const key: string = localStorage.key(index);
      if (key === null) continue;

      const isPreserved: boolean =
        PRESERVED_STORAGE_KEYS.includes(key) ||
        PRESERVED_STORAGE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));

      if (isPreserved) preserved.set(key, localStorage.getItem(key));
    }

    localStorage.clear();
    sessionStorage.clear();

    preserved.forEach((value, key) => localStorage.setItem(key, value));
  }

  checkDeployVersion(): void {
    if (!deployVersion) return;

    fetch(`${DEPLOY_VERSION_ASSET}?t=${Date.now()}`)
      .then((response: Response) => {
        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }
        return response.text();
      })
      .then((text: string) => {
        const serverVersion: string = text.match(DEPLOY_VERSION_PATTERN)?.[1];

        if (!serverVersion) {
          console.error(
            `[deploy-version] Could not parse a version out of ${DEPLOY_VERSION_ASSET}. Received:`,
            text.slice(0, 200),
          );
          return;
        }

        if (serverVersion !== deployVersion) {
          this._newVersionAvailable.next(true);
        }
      })
      .catch((error) => {
        console.error(
          `[deploy-version] Failed to read ${DEPLOY_VERSION_ASSET}:`,
          error,
        );
      });
  }
}
